import { useRouter } from "next/navigation";
import { useState, type JSX } from "react";
import { useSelector } from "react-redux";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  Divider,
} from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import ExpandTitle from "@/components/atoms/expander/expand-title";
import withMedicalFlow from "@/components/with-medical-flow-v2";
import { MedicalEdit } from "@/config/app-constant";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { RootState } from "@/store/root-reducer";

interface Props {
  brand: "staysure" | "avanti";
  medicalTitle?: string | null;
  medicalBtn?: string;
  editMedicalBtn?: string;
  isOpen: boolean;
  onToggle: () => void;
  newMedicalFlowEnabled: boolean;
}

function EditMedical({
  medicalTitle = "View or edit Medical Conditions",
  medicalBtn = "Add conditions",
  editMedicalBtn = "Add/edit conditions",
  isOpen,
  onToggle,
  brand,
  newMedicalFlowEnabled,
}: Readonly<Props>): JSX.Element {
  const router = useRouter();
  const [visibleConditions, setVisibleConditions] = useState<
    Record<string, boolean>
  >({});

  const { attributes } = useSelector((state: RootState) => state.quote);

  const travellers = attributes.travellerDetails?.traveller;
  const { t } = useWizardTranslation();

  const toggleConditionsVisibility = (travellerId: string) => {
    setVisibleConditions((prevState) => ({
      ...prevState,
      [travellerId]: !prevState[travellerId],
    }));
  };

  const renderSvgPlus = () => {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g id="Plus">
          <path
            id="Vector"
            d="M12 5V19M5 12H19"
            stroke="#306582"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      </svg>
    );
  };

  const getTravellerMedical = () => {
    if (!travellers) return false;
    return travellers.some((traveller) => traveller.medical !== undefined);
  };

  const ctaButtonLabel = getTravellerMedical() ? editMedicalBtn : medicalBtn;

  const { current } = useWizard();

  const handleAddBtn = () => {
    const hasConditions = getTravellerMedical();

    let path: string | undefined;
    if (hasConditions) {
      path = current.edges.medicalScreening;
    } else {
      path = newMedicalFlowEnabled
        ? current.edges.defaultMedicalFlow
        : current.edges.oldMedicalFlow;
    }

    router.push(path ?? "");
  };

  return (
    <div data-testid="edit-medical">
      <ExpandTitle
        title={medicalTitle}
        setIsShow={onToggle}
        initShow={isOpen}
        id="edit-quote-medical-toggle"
        brand={brand}
      />
      {isOpen && (
        <div className="flex flex-col">
          <div className="w-full flex flex-col ">
            {travellers?.map((person) => {
              return (
                <div key={person.encryptedId}>
                  <div>
                    <button
                      type="button"
                      className="w-full transition duration-300 flex items-center justify-between sm:mb-spacing-s"
                      data-testid="accordion-toggle-btn"
                      onClick={() =>
                        toggleConditionsVisibility(person.encryptedId ?? "")
                      }
                    >
                      <div className="text-surface-neutral-option-10 font-sub-section-header-font md:font-bold sm:font-semibold sm:text-[16px] md:text-[18px]">
                        <p
                          data-testid="paragaph-testid"
                          className=" false"
                          id="paragaph-test-id3"
                        >
                          {`${person.firstName} ${person.lastName}`}
                        </p>
                      </div>
                      <div className="w-[24px] h-[24px]">
                        {visibleConditions[person.encryptedId ?? ""] ? (
                          <ChevronUpIcon brand={brand} />
                        ) : (
                          <ChevronDownIcon brand={brand} />
                        )}
                      </div>
                    </button>
                  </div>
                  {visibleConditions[person.encryptedId ?? ""] && (
                    <div>
                      {person.medical ? (
                        <ul className="my-spacing-s !p-0">
                          {person.medical?.condition
                            .split("|")
                            .map((condition, index) => (
                              <li
                                className="list-none text-functional-subtle mb-spacing-m last:mb-spacing-s font-sub-section-header-font"
                                key={index}
                              >
                                {condition}
                                <Divider
                                  thickness={1}
                                  className="!mt-spacing-m !mb-0"
                                  brand={brand}
                                />
                              </li>
                            ))}
                        </ul>
                      ) : (
                        <div className="odd:mb-spacing-s">
                          <span className="text-functional-subtle font-sub-section-header-font">
                            {t("no-conditions-added", "No conditions added")}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="w-[218px] md:mt-spacing-s lg:mb-spacing-m md:mb-spacing-xs sm:mt-spacing-xxs sm:mb-spacing-s">
            <LabelButtonWrapper
              label={ctaButtonLabel}
              id="edit-quote-medical"
              hierarchy="tertiary"
              isLeftIcon
              className="!px-0 !leading-[16px] !font-semibold font-sub-section-header-font"
              leftIcon={renderSvgPlus()}
              //   isDisabled={loading}
              onClick={handleAddBtn}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default withMedicalFlow(EditMedical);
